import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  deleteDictionaryItem,
  getApiErrorMessage,
  listCategories,
  listCounties,
  listCountries,
  listLocationBestSeasons,
  listLocationDifficulties,
  listLocationRoadTypes,
  listNewsCategories,
  listNewsTags,
  listEventTypes,
  listRegions,
  listRouteCategories,
  listRouteDifficulties,
  listRouteTags,
  listSettlements,
  listTags,
  patchDictionaryItem,
  type DictionaryGroup,
  type LocationDictionaryItem,
} from '../lib/api';
import { DICTIONARIES_TEMPLATE } from '../templates/dictionariesTemplate';

type ModuleKey = 'locations' | 'routes' | 'news' | 'events';
type DictionaryGroupKey = DictionaryGroup;

type GroupConfig = {
  key: DictionaryGroupKey;
  label: string;
  icon: string;
  module: ModuleKey;
  statusSupported: boolean;
};

const MODULE_LABELS: Record<ModuleKey, string> = {
  locations: 'Locations',
  routes: 'Routes',
  news: 'News',
  events: 'Events',
};

const GROUPS: GroupConfig[] = [
  { key: 'countries', label: 'Countries', icon: 'public', module: 'locations', statusSupported: true },
  { key: 'regions', label: 'Regions', icon: 'map', module: 'locations', statusSupported: true },
  { key: 'counties', label: 'Counties', icon: 'layers', module: 'locations', statusSupported: true },
  { key: 'settlements', label: 'Settlements', icon: 'location_city', module: 'locations', statusSupported: true },
  { key: 'tags', label: 'Tags', icon: 'tag', module: 'locations', statusSupported: true },
  { key: 'location-categories', label: 'Categories', icon: 'interests', module: 'locations', statusSupported: true },
  { key: 'location-road-types', label: 'Road Types', icon: 'traffic', module: 'locations', statusSupported: true },
  { key: 'location-difficulties', label: 'Difficulties', icon: 'handyman', module: 'locations', statusSupported: true },
  { key: 'location-best-seasons', label: 'Best Seasons', icon: 'wb_sunny', module: 'locations', statusSupported: true },
  { key: 'route-categories', label: 'Categories', icon: 'route', module: 'routes', statusSupported: true },
  { key: 'route-difficulties', label: 'Difficulties', icon: 'handyman', module: 'routes', statusSupported: true },
  { key: 'route-tags', label: 'Tags', icon: 'sell', module: 'routes', statusSupported: true },
  { key: 'news-categories', label: 'Categories', icon: 'category', module: 'news', statusSupported: true },
  { key: 'news-tags', label: 'Tags', icon: 'sell', module: 'news', statusSupported: true },
  { key: 'event-types', label: 'Event Types', icon: 'event', module: 'events', statusSupported: true },
];

const GROUP_CONFIG: Record<DictionaryGroupKey, GroupConfig> = GROUPS.reduce(
  (acc, group) => {
    acc[group.key] = group;
    return acc;
  },
  {} as Record<DictionaryGroupKey, GroupConfig>
);

const MODULE_GROUPS: Record<ModuleKey, DictionaryGroupKey[]> = {
  locations: GROUPS.filter((group) => group.module === 'locations').map((group) => group.key),
  routes: GROUPS.filter((group) => group.module === 'routes').map((group) => group.key),
  news: GROUPS.filter((group) => group.module === 'news').map((group) => group.key),
  events: GROUPS.filter((group) => group.module === 'events').map((group) => group.key),
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export default function DictionariesPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => DICTIONARIES_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const moduleButtons = Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-module]'));
    const groupsTitle = root.querySelector<HTMLElement>('#dictionaries-groups-title');
    const groupsContainer = root.querySelector<HTMLElement>('#dictionaries-groups');
    const searchInput = root.querySelector<HTMLInputElement>('#dictionaries-search-input');
    const addEntryBtn = root.querySelector<HTMLAnchorElement>('#dictionaries-add-entry-btn');
    const statusFilter = root.querySelector<HTMLSelectElement>('#dictionaries-status-filter');
    const sortFilter = root.querySelector<HTMLSelectElement>('#dictionaries-sort-filter');
    const tbody = root.querySelector<HTMLTableSectionElement>('#dictionaries-table-body');
    const meta = root.querySelector<HTMLElement>('#dictionaries-table-meta');
    const prevBtn = root.querySelector<HTMLButtonElement>('#dictionaries-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#dictionaries-next-btn');
    const pageIndicator = root.querySelector<HTMLElement>('#dictionaries-page-indicator');
    const selectAllCheckbox = root.querySelector<HTMLInputElement>('#dictionaries-select-all');
    const bulkActivateBtn = root.querySelector<HTMLButtonElement>('#dictionaries-bulk-activate');
    const bulkDeactivateBtn = root.querySelector<HTMLButtonElement>('#dictionaries-bulk-deactivate');
    const bulkDeleteBtn = root.querySelector<HTMLButtonElement>('#dictionaries-bulk-delete');
    const modal = root.querySelector<HTMLElement>('#dictionaries-modal');
    const modalBackdrop = root.querySelector<HTMLElement>('#dictionaries-modal-backdrop');
    const modalTitle = root.querySelector<HTMLElement>('#dictionaries-modal-title');
    const modalMessage = root.querySelector<HTMLElement>('#dictionaries-modal-message');
    const modalCancel = root.querySelector<HTMLButtonElement>('#dictionaries-modal-cancel');
    const modalConfirm = root.querySelector<HTMLButtonElement>('#dictionaries-modal-confirm');
    const toast = root.querySelector<HTMLElement>('#dictionaries-toast');

    if (
      !moduleButtons.length ||
      !groupsTitle ||
      !groupsContainer ||
      !searchInput ||
      !addEntryBtn ||
      !statusFilter ||
      !sortFilter ||
      !tbody ||
      !meta ||
      !prevBtn ||
      !nextBtn ||
      !pageIndicator ||
      !selectAllCheckbox ||
      !bulkActivateBtn ||
      !bulkDeactivateBtn ||
      !bulkDeleteBtn ||
      !modal ||
      !modalBackdrop ||
      !modalTitle ||
      !modalMessage ||
      !modalCancel ||
      !modalConfirm ||
      !toast
    ) {
      return;
    }

    let disposed = false;
    let searchTimer: number | null = null;
    let toastTimer: number | null = null;
    let currentPage = 1;
    const pageSize = 20;

    const dictionaries: Record<DictionaryGroupKey, LocationDictionaryItem[]> = {
      countries: [],
      regions: [],
      counties: [],
      settlements: [],
      tags: [],
      'location-categories': [],
      'location-road-types': [],
      'location-difficulties': [],
      'location-best-seasons': [],
      'route-categories': [],
      'route-difficulties': [],
      'route-tags': [],
      'news-categories': [],
      'news-tags': [],
      'event-types': [],
    };

    const params = new URLSearchParams(window.location.search);
    const groupParam = params.get('group');
    const moduleParam = params.get('module');

    let activeModule: ModuleKey =
      moduleParam === 'routes' || moduleParam === 'news' || moduleParam === 'events' ? moduleParam : 'locations';
    let activeGroup: DictionaryGroupKey =
      groupParam && GROUP_CONFIG[groupParam as DictionaryGroupKey]
        ? (groupParam as DictionaryGroupKey)
        : MODULE_GROUPS[activeModule][0];

    if (GROUP_CONFIG[activeGroup].module !== activeModule) {
      activeModule = GROUP_CONFIG[activeGroup].module;
    }

    const selectedIds = new Set<number>();
    let renderedItems: LocationDictionaryItem[] = [];

    const showToast = (message: string) => {
      if (toastTimer) {
        window.clearTimeout(toastTimer);
      }
      toast.textContent = message;
      toast.classList.remove('hidden');
      toastTimer = window.setTimeout(() => {
        toast.classList.add('hidden');
      }, 2800);
    };

    const confirmAction = (title: string, message: string, confirmLabel = 'Confirm'): Promise<boolean> =>
      new Promise((resolve) => {
        modalTitle.textContent = title;
        modalMessage.textContent = message;
        modalConfirm.textContent = confirmLabel;
        modal.classList.remove('hidden');

        const close = (result: boolean) => {
          modal.classList.add('hidden');
          modalCancel.removeEventListener('click', onCancel);
          modalConfirm.removeEventListener('click', onConfirm);
          modalBackdrop.removeEventListener('click', onCancel);
          resolve(result);
        };

        const onCancel = () => close(false);
        const onConfirm = () => close(true);

        modalCancel.addEventListener('click', onCancel);
        modalConfirm.addEventListener('click', onConfirm);
        modalBackdrop.addEventListener('click', onCancel);
      });

    const setModuleButtonState = () => {
      for (const button of moduleButtons) {
        const key = button.dataset.module as ModuleKey | undefined;
        if (!key) continue;
        if (key === activeModule) {
          button.classList.remove('text-slate-500', 'hover:text-slate-700', 'border-transparent');
          button.classList.add('text-primary', 'border-primary', 'font-semibold');
        } else {
          button.classList.remove('text-primary', 'border-primary', 'font-semibold');
          button.classList.add('text-slate-500', 'hover:text-slate-700', 'border-transparent');
        }
      }
    };

    const renderGroups = () => {
      const groups = MODULE_GROUPS[activeModule];
      groupsTitle.textContent = `${MODULE_LABELS[activeModule]} Groups`;

      groupsContainer.innerHTML = groups
        .map((groupKey) => {
          const group = GROUP_CONFIG[groupKey];
          const count = dictionaries[groupKey].length;
          const isActive = groupKey === activeGroup;

          return `<button class="dictionary-group w-full flex items-center justify-between px-3 py-2 rounded-lg ${
            isActive ? 'bg-primary/5 text-primary' : 'text-slate-600 hover:bg-slate-100 transition-colors'
          }" data-group="${groupKey}" type="button"><div class="flex items-center gap-3"><span class="material-symbols-outlined text-lg">${group.icon}</span><span class="text-sm ${
            isActive ? 'font-semibold' : 'font-medium'
          }">${escapeHtml(group.label)}</span></div><span class="text-[10px] px-2 py-0.5 rounded-full font-bold ${
            isActive ? 'bg-primary/20' : 'bg-slate-200 text-slate-500'
          }">${count}</span></button>`;
        })
        .join('');
    };

    const updateSelectionUi = () => {
      const selectableIds = renderedItems.map((item) => item.id);
      const selectedVisibleCount = selectableIds.filter((id) => selectedIds.has(id)).length;
      const selectableCount = selectableIds.length;

      selectAllCheckbox.checked = selectableCount > 0 && selectedVisibleCount === selectableCount;
      selectAllCheckbox.indeterminate = selectedVisibleCount > 0 && selectedVisibleCount < selectableCount;

      const hasSelection = selectedIds.size > 0;
      const statusSupported = GROUP_CONFIG[activeGroup].statusSupported;
      bulkActivateBtn.disabled = !hasSelection || !statusSupported;
      bulkDeactivateBtn.disabled = !hasSelection || !statusSupported;
      bulkDeleteBtn.disabled = !hasSelection;

      for (const btn of [bulkActivateBtn, bulkDeactivateBtn, bulkDeleteBtn]) {
        btn.classList.toggle('opacity-50', btn.disabled);
        btn.classList.toggle('cursor-not-allowed', btn.disabled);
      }
    };

    const render = () => {
      const sourceItems = dictionaries[activeGroup] ?? [];
      const term = searchInput.value.trim().toLowerCase();
      let filteredItems = sourceItems.filter((item) => {
        if (!term) return true;
        const name = item.name?.toLowerCase() ?? '';
        const slug = item.slug?.toLowerCase() ?? '';
        return name.includes(term) || slug.includes(term);
      });

      const statusSupported = GROUP_CONFIG[activeGroup].statusSupported;
      const statusValue = statusFilter.value;
      if (statusSupported && statusValue !== 'all') {
        filteredItems = filteredItems.filter((item) => {
          const isActive = item.is_active ?? true;
          return statusValue === 'active' ? isActive : !isActive;
        });
      }

      const sortValue = sortFilter.value;
      filteredItems.sort((a, b) => {
        if (sortValue === 'sort_order_asc') return (a.sort_order ?? 999999) - (b.sort_order ?? 999999);
        if (sortValue === 'name_desc') return (b.name ?? '').localeCompare(a.name ?? '');
        if (sortValue === 'slug_asc') return (a.slug ?? '').localeCompare(b.slug ?? '');
        if (sortValue === 'updated_desc') {
          const aTime = a.updated_at ? Date.parse(a.updated_at) : 0;
          const bTime = b.updated_at ? Date.parse(b.updated_at) : 0;
          return bTime - aTime;
        }
        return (a.name ?? '').localeCompare(b.name ?? '');
      });
      const totalFiltered = filteredItems.length;
      const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
      currentPage = Math.min(currentPage, totalPages);
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      const items = filteredItems.slice(start, end);
      renderedItems = items;

      if (!items.length) {
        tbody.innerHTML = '<tr><td class="p-6 text-sm text-slate-500" colspan="8">No dictionary entries found.</td></tr>';
      } else {
        tbody.innerHTML = items
          .map((item) => {
            const statusCell = statusSupported
              ? item.is_active ?? true
                ? '<span class="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full"><span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active</span>'
                : '<span class="inline-flex items-center gap-1.5 text-xs text-slate-500 font-bold bg-slate-200 px-2 py-0.5 rounded-full"><span class="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inactive</span>'
              : '<span class="text-sm text-slate-400">N/A</span>';

            return `<tr class="hover:bg-slate-50 transition-colors"><td class="p-4"><input class="rounded border-slate-300 text-primary focus:ring-primary" data-select-id="${
              item.id
            }" type="checkbox" ${selectedIds.has(item.id) ? 'checked' : ''}/></td><td class="p-4"><span class="text-sm font-semibold">${escapeHtml(
              item.name ?? ''
            )}</span></td><td class="p-4 text-sm text-slate-600 font-mono">${escapeHtml(
              item.slug ?? '-'
            )}</td><td class="p-4 text-xs"><span class="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">${escapeHtml(
              MODULE_LABELS[activeModule]
            )}</span></td><td class="p-4">${statusCell}</td><td class="p-4 text-sm text-center font-medium">${
              item.sort_order ?? '-'
            }</td><td class="p-4 text-sm text-slate-500">${formatDate(item.updated_at)}</td><td class="p-4 text-right"><div class="flex items-center justify-end gap-1"><button class="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors" data-action="edit" data-id="${
              item.id
            }" type="button"><span class="material-symbols-outlined text-lg">edit</span></button><button class="p-1.5 hover:bg-rose-500/10 rounded-lg text-slate-400 hover:text-rose-600 transition-colors" data-action="delete" data-id="${
              item.id
            }" type="button"><span class="material-symbols-outlined text-lg">delete</span></button></div></td></tr>`;
          })
          .join('');
      }

      statusFilter.disabled = !statusSupported;
      statusFilter.classList.toggle('opacity-60', !statusSupported);
      statusFilter.classList.toggle('cursor-not-allowed', !statusSupported);

      addEntryBtn.href = `/dictionaries/create?module=${encodeURIComponent(activeModule)}&group=${encodeURIComponent(activeGroup)}`;
      const from = totalFiltered === 0 ? 0 : start + 1;
      const to = totalFiltered === 0 ? 0 : Math.min(start + items.length, totalFiltered);
      meta.textContent = `Showing ${from}-${to} of ${totalFiltered} entries`;
      pageIndicator.textContent = `Page ${currentPage}`;
      prevBtn.disabled = currentPage <= 1;
      nextBtn.disabled = currentPage >= totalPages;
      setModuleButtonState();
      renderGroups();
      updateSelectionUi();
    };

    const load = async () => {
      tbody.innerHTML = '<tr><td class="p-6 text-sm text-slate-500" colspan="8">Loading dictionaries...</td></tr>';
      try {
        const [
          countries,
          regions,
          counties,
          settlements,
          tags,
          locationCategories,
          roadTypes,
          difficulties,
          bestSeasons,
          routeCategories,
          routeDifficulties,
          routeTags,
          newsCategories,
          newsTags,
          eventTypes,
        ] = await Promise.all([
          listCountries(),
          listRegions(),
          listCounties(),
          listSettlements(),
          listTags(),
          listCategories(),
          listLocationRoadTypes(),
          listLocationDifficulties(),
          listLocationBestSeasons(),
          listRouteCategories(),
          listRouteDifficulties(),
          listRouteTags(),
          listNewsCategories(),
          listNewsTags(),
          listEventTypes().then((result) => result.results),
        ]);

        if (disposed) return;

        dictionaries.countries = countries;
        dictionaries.regions = regions;
        dictionaries.counties = counties;
        dictionaries.settlements = settlements;
        dictionaries.tags = tags;
        dictionaries['location-categories'] = locationCategories;
        dictionaries['location-road-types'] = roadTypes;
        dictionaries['location-difficulties'] = difficulties;
        dictionaries['location-best-seasons'] = bestSeasons;
        dictionaries['route-categories'] = routeCategories;
        dictionaries['route-difficulties'] = routeDifficulties;
        dictionaries['route-tags'] = routeTags;
        dictionaries['news-categories'] = newsCategories;
        dictionaries['news-tags'] = newsTags;
        dictionaries['event-types'] = eventTypes;

        render();
      } catch (error) {
        if (disposed) return;
        const message = getApiErrorMessage(error, 'Failed to load dictionaries.');
        tbody.innerHTML = `<tr><td class="p-6 text-sm text-rose-600" colspan="8">${escapeHtml(message)}</td></tr>`;
        meta.textContent = 'Failed to load entries';
      }
    };

    const onModuleClick = (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const key = button.dataset.module as ModuleKey | undefined;
      if (!key || key === activeModule) return;
      activeModule = key;
      activeGroup = MODULE_GROUPS[activeModule][0];
      currentPage = 1;
      selectedIds.clear();
      render();
    };

    const onGroupClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-group]');
      if (!button) return;
      const key = button.dataset.group as DictionaryGroupKey | undefined;
      if (!key || !GROUP_CONFIG[key]) return;
      activeGroup = key;
      currentPage = 1;
      selectedIds.clear();
      render();
    };

    const onSearchInput = () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        currentPage = 1;
        render();
      }, 200);
    };
    const onPrevPage = () => {
      if (currentPage <= 1) return;
      currentPage -= 1;
      render();
    };
    const onNextPage = () => {
      currentPage += 1;
      render();
    };

    const onTableClick = async (event: Event) => {
      const target = event.target as HTMLElement;
      const actionButton = target.closest<HTMLButtonElement>('button[data-action][data-id]');
      if (!actionButton) return;
      const action = actionButton.dataset.action;
      const id = Number(actionButton.dataset.id);
      if (!Number.isFinite(id) || id <= 0) return;

      if (action === 'edit') {
        window.location.href = `/dictionaries/create?module=${encodeURIComponent(activeModule)}&group=${encodeURIComponent(activeGroup)}&id=${id}`;
        return;
      }

      if (action !== 'delete') return;
      const ok = await confirmAction('Delete Entry', 'Delete this dictionary entry? This action cannot be undone.', 'Delete');
      if (!ok) return;

      actionButton.disabled = true;
      try {
        await deleteDictionaryItem(activeGroup, id);
        dictionaries[activeGroup] = dictionaries[activeGroup].filter((entry) => entry.id !== id);
        selectedIds.delete(id);
        render();
      } catch (error) {
        const message = getApiErrorMessage(error, 'Failed to delete dictionary entry.');
        showToast(message);
        actionButton.disabled = false;
      }
    };

    const onTableCheckboxChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (!target.matches('input[data-select-id]')) return;
      const id = Number(target.dataset.selectId);
      if (!Number.isFinite(id) || id <= 0) return;
      if (target.checked) selectedIds.add(id);
      else selectedIds.delete(id);
      updateSelectionUi();
    };

    const onSelectAllChange = () => {
      const checked = selectAllCheckbox.checked;
      for (const item of renderedItems) {
        if (checked) selectedIds.add(item.id);
        else selectedIds.delete(item.id);
      }
      render();
    };

    const applyBulkActiveState = async (isActive: boolean) => {
      if (!selectedIds.size) return;
      if (!GROUP_CONFIG[activeGroup].statusSupported) {
        showToast('This dictionary does not support status changes.');
        return;
      }
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(ids.map((id) => patchDictionaryItem(activeGroup, id, { is_active: isActive })));

      let failed = 0;
      for (let i = 0; i < ids.length; i += 1) {
        if (results[i].status === 'fulfilled') {
          const item = dictionaries[activeGroup].find((entry) => entry.id === ids[i]);
          if (item) item.is_active = isActive;
        } else {
          failed += 1;
        }
      }

      render();
      if (failed) showToast(`Updated with ${failed} failed request(s).`);
    };

    const onBulkDelete = async () => {
      if (!selectedIds.size) return;
      const ok = await confirmAction(
        'Delete Selected',
        `Delete ${selectedIds.size} selected item(s)? This action cannot be undone.`,
        'Delete'
      );
      if (!ok) return;

      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(ids.map((id) => deleteDictionaryItem(activeGroup, id)));
      let failed = 0;
      for (let i = 0; i < ids.length; i += 1) {
        if (results[i].status === 'fulfilled') {
          dictionaries[activeGroup] = dictionaries[activeGroup].filter((entry) => entry.id !== ids[i]);
          selectedIds.delete(ids[i]);
        } else {
          failed += 1;
        }
      }

      render();
      if (failed) showToast(`Deleted with ${failed} failed request(s).`);
    };

    moduleButtons.forEach((button) => button.addEventListener('click', onModuleClick));
    groupsContainer.addEventListener('click', onGroupClick);
    searchInput.addEventListener('input', onSearchInput);
    statusFilter.addEventListener('change', () => {
      currentPage = 1;
      render();
    });
    sortFilter.addEventListener('change', () => {
      currentPage = 1;
      render();
    });
    tbody.addEventListener('click', onTableClick);
    tbody.addEventListener('change', onTableCheckboxChange);
    selectAllCheckbox.addEventListener('change', onSelectAllChange);
    prevBtn.addEventListener('click', onPrevPage);
    nextBtn.addEventListener('click', onNextPage);
    bulkActivateBtn.addEventListener('click', () => {
      void applyBulkActiveState(true);
    });
    bulkDeactivateBtn.addEventListener('click', () => {
      void applyBulkActiveState(false);
    });
    bulkDeleteBtn.addEventListener('click', () => {
      void onBulkDelete();
    });

    renderGroups();
    setModuleButtonState();
    void load();

    return () => {
      disposed = true;
      if (searchTimer) window.clearTimeout(searchTimer);
      if (toastTimer) window.clearTimeout(toastTimer);
      moduleButtons.forEach((button) => button.removeEventListener('click', onModuleClick));
      groupsContainer.removeEventListener('click', onGroupClick);
      searchInput.removeEventListener('input', onSearchInput);
      tbody.removeEventListener('click', onTableClick);
      tbody.removeEventListener('change', onTableCheckboxChange);
      selectAllCheckbox.removeEventListener('change', onSelectAllChange);
      prevBtn.removeEventListener('click', onPrevPage);
      nextBtn.removeEventListener('click', onNextPage);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Dictionaries</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
